import React, { useState, useEffect } from 'react';
import { apiService } from '../../services/api';

interface Meal {
  id: string;
  plan_date: string;
  meal_type: string;
  title: string;
  description?: string;
  ingredients?: string;
  calories?: number;
  prep_time_minutes?: number;
  is_completed: boolean;
}

const MEAL_TYPES = ['breakfast', 'lunch', 'dinner', 'snack'];
const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const MealPlanner: React.FC = () => {
  const [meals, setMeals] = useState<Meal[]>([]);
  const [loading, setLoading] = useState(true);
  const [weekStart, setWeekStart] = useState(() => {
    const today = new Date();
    const day = today.getDay();
    const diff = today.getDate() - day;
    return new Date(today.setDate(diff)).toISOString().split('T')[0];
  });
  const [editingCell, setEditingCell] = useState<{ date: string; mealType: string } | null>(null);
  const [editValue, setEditValue] = useState('');

  useEffect(() => {
    fetchMeals();
  }, [weekStart]);

  const getWeekDates = () => {
    const dates = [];
    const start = new Date(weekStart);
    for (let i = 0; i < 7; i++) {
      const date = new Date(start);
      date.setDate(start.getDate() + i);
      dates.push(date.toISOString().split('T')[0]);
    }
    return dates;
  };

  const fetchMeals = async () => {
    setLoading(true);
    try {
      const endDate = new Date(weekStart);
      endDate.setDate(endDate.getDate() + 6);
      const response = await apiService.getMeals(weekStart, endDate.toISOString().split('T')[0]);
      if (response.success) {
        setMeals(response.meals || []);
      }
    } catch (error) {
      console.error('Failed to fetch meals:', error);
    } finally {
      setLoading(false);
    }
  };

  const getMeal = (date: string, mealType: string) => {
    return meals.find(m => m.plan_date.split('T')[0] === date && m.meal_type === mealType);
  };

  const handleCellClick = (date: string, mealType: string) => {
    const meal = getMeal(date, mealType);
    setEditingCell({ date, mealType });
    setEditValue(meal?.title || '');
  };

  const handleSave = async () => {
    if (!editingCell || !editValue.trim()) {
      setEditingCell(null);
      return;
    }

    try {
      await apiService.saveMeal({
        date: editingCell.date,
        mealType: editingCell.mealType,
        title: editValue.trim()
      });
      await fetchMeals();
    } catch (error) {
      console.error('Failed to save meal:', error);
    }
    setEditingCell(null);
    setEditValue('');
  };

  const handleToggleComplete = async (meal: Meal) => {
    try {
      await apiService.toggleMealComplete(meal.id);
      setMeals(meals.map(m => m.id === meal.id ? { ...m, is_completed: !m.is_completed } : m));
    } catch (error) {
      console.error('Failed to toggle meal:', error);
    }
  };

  const handleDelete = async (meal: Meal) => {
    try {
      await apiService.deleteMeal(meal.id);
      setMeals(meals.filter(m => m.id !== meal.id));
    } catch (error) {
      console.error('Failed to delete meal:', error);
    }
  };

  const navigateWeek = (direction: number) => {
    const newStart = new Date(weekStart);
    newStart.setDate(newStart.getDate() + (direction * 7));
    setWeekStart(newStart.toISOString().split('T')[0]);
  };

  const weekDates = getWeekDates();

  if (loading) {
    return (
      <div style={{ padding: '40px', textAlign: 'center' }}>
        <div style={{ width: 40, height: 40, border: '4px solid #e5e7eb', borderTop: '4px solid #667eea', borderRadius: '50%', margin: '0 auto', animation: 'spin 1s linear infinite' }}></div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  return (
    <div style={{ padding: '20px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#1f2937' }}>Meal Planner</h2>
          <p style={{ margin: '4px 0 0 0', color: '#6b7280', fontSize: '0.875rem' }}>Plan your meals for the week</p>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <button onClick={() => navigateWeek(-1)} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer' }}>
            Previous
          </button>
          <span style={{ padding: '8px 16px', fontWeight: 600, color: '#374151' }}>
            {new Date(weekStart).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {new Date(weekDates[6]).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
          <button onClick={() => navigateWeek(1)} style={{ padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: '8px', backgroundColor: 'white', cursor: 'pointer' }}>
            Next
          </button>
        </div>
      </div>

      {/* Usage Tips */}
      <div style={{ 
        padding: '16px', 
        backgroundColor: '#ecfdf5', 
        borderRadius: '10px', 
        marginBottom: '20px',
        border: '1px solid #6ee7b7'
      }}>
        <div style={{ fontSize: '0.875rem', color: '#065f46', fontWeight: 600, marginBottom: '8px' }}>
          Tips for Meal Planning
        </div>
        <ul style={{ margin: 0, paddingLeft: '20px', color: '#047857', fontSize: '0.8rem', lineHeight: 1.6 }}>
          <li>Click any cell to add a meal - quick entries like "Pasta" or detailed recipes</li>
          <li>Mark meals as done to track what you've actually cooked</li>
          <li>Plan snacks for busy days or when kids are home</li>
          <li>Use the week navigation to prep meals in advance</li>
        </ul>
      </div>

      {/* Grid */}
      <div style={{ backgroundColor: 'white', borderRadius: '12px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        {/* Header Row */}
        <div style={{ display: 'grid', gridTemplateColumns: '100px repeat(7, 1fr)', borderBottom: '2px solid #e5e7eb' }}>
          <div style={{ padding: '12px', backgroundColor: '#f9fafb', fontWeight: 600, color: '#6b7280' }}></div>
          {weekDates.map((date, i) => {
            const isToday = date === new Date().toISOString().split('T')[0];
            return (
              <div key={date} style={{ 
                padding: '12px', 
                textAlign: 'center', 
                backgroundColor: isToday ? '#eff6ff' : '#f9fafb',
                borderLeft: '1px solid #e5e7eb'
              }}>
                <div style={{ fontWeight: 600, color: isToday ? '#2563eb' : '#374151' }}>{DAYS[i]}</div>
                <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>{new Date(date).getDate()}</div>
              </div>
            );
          })}
        </div>

        {/* Meal Rows */}
        {MEAL_TYPES.map(mealType => (
          <div key={mealType} style={{ display: 'grid', gridTemplateColumns: '100px repeat(7, 1fr)', borderBottom: '1px solid #e5e7eb' }}>
            <div style={{ 
              padding: '12px', 
              fontWeight: 600, 
              color: '#374151', 
              textTransform: 'capitalize',
              backgroundColor: '#f9fafb',
              display: 'flex',
              alignItems: 'center'
            }}>
              {mealType === 'breakfast' && 'sunrise '}
              {mealType === 'lunch' && 'sun '}
              {mealType === 'dinner' && 'moon '}
              {mealType === 'snack' && 'apple '}
              {mealType}
            </div>
            {weekDates.map(date => {
              const meal = getMeal(date, mealType);
              const isEditing = editingCell?.date === date && editingCell?.mealType === mealType;

              return (
                <div
                  key={`${date}-${mealType}`}
                  onClick={() => !isEditing && handleCellClick(date, mealType)}
                  style={{
                    padding: '8px',
                    minHeight: '60px',
                    borderLeft: '1px solid #e5e7eb',
                    cursor: 'pointer',
                    backgroundColor: meal?.is_completed ? '#f0fdf4' : 'white',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => { if (!meal) e.currentTarget.style.backgroundColor = '#f9fafb'; }}
                  onMouseLeave={(e) => { if (!meal || !meal.is_completed) e.currentTarget.style.backgroundColor = meal?.is_completed ? '#f0fdf4' : 'white'; }}
                >
                  {isEditing ? (
                    <input
                      autoFocus
                      value={editValue}
                      onChange={(e) => setEditValue(e.target.value)}
                      onBlur={handleSave}
                      onKeyDown={(e) => e.key === 'Enter' && handleSave()}
                      style={{
                        width: '100%',
                        padding: '6px',
                        border: '2px solid #667eea',
                        borderRadius: '6px',
                        fontSize: '0.8rem'
                      }}
                      placeholder="Enter meal..."
                    />
                  ) : meal ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ 
                        fontSize: '0.8rem', 
                        fontWeight: 500,
                        color: meal.is_completed ? '#16a34a' : '#374151',
                        textDecoration: meal.is_completed ? 'line-through' : 'none'
                      }}>
                        {meal.title}
                      </div>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleToggleComplete(meal); }}
                          style={{
                            padding: '2px 6px',
                            fontSize: '0.65rem',
                            backgroundColor: meal.is_completed ? '#dcfce7' : '#e5e7eb',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                          }}
                        >
                          {meal.is_completed ? 'check' : 'Done'}
                        </button>
                        <button
                          onClick={(e) => { e.stopPropagation(); handleDelete(meal); }}
                          style={{
                            padding: '2px 6px',
                            fontSize: '0.65rem',
                            backgroundColor: '#fee2e2',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer',
                            color: '#dc2626'
                          }}
                        >
                          X
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div style={{ color: '#d1d5db', fontSize: '0.75rem', textAlign: 'center', paddingTop: '16px' }}>
                      + Add
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Stats */}
      <div style={{ marginTop: '20px', display: 'flex', gap: '16px' }}>
        <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '10px', flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#667eea' }}>
            {meals.length}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Meals Planned</div>
        </div>
        <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '10px', flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#22c55e' }}>
            {meals.filter(m => m.is_completed).length}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Completed</div>
        </div>
        <div style={{ padding: '16px', backgroundColor: 'white', borderRadius: '10px', flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f59e0b' }}>
            {28 - meals.length}
          </div>
          <div style={{ fontSize: '0.75rem', color: '#6b7280' }}>Empty Slots</div>
        </div>
      </div>
    </div>
  );
};

export default MealPlanner;
